/**
 * ChipSync Technology Solutions — Predictive Search Component
 * Full-screen AJAX search with live results and suggestions.
 * IDs match header.liquid search overlay structure.
 */

class PredictiveSearch {
  constructor() {
    this.overlay    = document.getElementById('search-overlay');       // .search-overlay in header.liquid
    this.toggle     = document.getElementById('search-toggle-btn');    // search icon button in nav
    this.closeBtn   = document.getElementById('search-close-btn');     // ✕ close button in overlay
    this.input      = document.getElementById('search-input');          // text input
    this.resultsEl  = document.getElementById('search-results-panel'); // live results container
    this.suggBlock  = document.querySelector('.search-overlay__suggestions'); // popular suggestions
    this.timer      = null;

    if (!this.overlay || !this.toggle) return;
    this.init();
  }

  init() {
    // Open overlay
    this.toggle.addEventListener('click', () => this.open());

    // Close overlay via ✕ button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Close on backdrop click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
        this.close();
      }
    });

    // Debounced search on input
    if (this.input) {
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 300);
      });
    }
  }

  open() {
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input && this.input.focus(), 100);
  }

  close() {
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.input) this.input.value = '';
    if (this.resultsEl) this.resultsEl.innerHTML = '';
    if (this.suggBlock) this.suggBlock.style.display = '';
  }

  async search() {
    if (!this.input) return;
    const query = this.input.value.trim();

    if (query.length < 2) {
      if (this.resultsEl) this.resultsEl.innerHTML = '';
      if (this.suggBlock) this.suggBlock.style.display = '';
      return;
    }

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,collection&resources[limit]=6&resources[order]=price-ascending`;
      const resp = await fetch(url);
      const data = await resp.json();
      this.renderResults(data.resources?.results || {});
    } catch (err) {
      console.error('[ChipSync Search] Failed:', err);
    }
  }

  renderResults(results) {
    const products = results.products || [];
    const pages    = results.pages    || [];

    // Hide suggestions when showing results
    if (this.suggBlock) {
      this.suggBlock.style.display = (products.length === 0 && pages.length === 0) ? '' : 'none';
    }

    if (!this.resultsEl) return;

    if (products.length === 0 && pages.length === 0) {
      this.resultsEl.innerHTML = '<div class="search-empty">No results found for your search.</div>';
      return;
    }

    let html = '';

    if (products.length > 0) {
      html += '<div class="search-section"><p class="search-section-title">Products</p><div class="search-item-grid">';
      products.forEach(p => {
        const img = p.image ? `<img src="${p.image}" width="50" height="50" alt="${this._esc(p.title)}" loading="lazy">` : '<span class="search-item-placeholder">📦</span>';
        html += `
          <a href="${p.url}" class="search-item">
            ${img}
            <div class="search-item-info">
              <span class="search-item-title">${this._esc(p.title)}</span>
              <span class="search-item-vendor">${this._esc(p.vendor || '')}</span>
            </div>
          </a>`;
      });
      html += '</div></div>';
    }

    if (pages.length > 0) {
      html += '<div class="search-section"><p class="search-section-title">Resources / Solutions</p><div class="search-item-list">';
      pages.forEach(pg => {
        html += `<a href="${pg.url}" class="search-item-link">${this._esc(pg.title)} <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg></a>`;
      });
      html += '</div></div>';
    }

    this.resultsEl.innerHTML = html;
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new PredictiveSearch();
});
